import React, { useContext, useEffect, useState, useRef } from "react";
import { AuthContext } from "context/AuthContext";
import { OrganizationContext } from "context/OrganizationContext";
import {
  createUser,
  deleteUser,
  updateUser,
  fetchSites,
  getUser,
} from "lib/actions";
import { showToast, PhoneInput } from "ui/components/common";
import { mutate } from "swr";
import { USER_ROLE } from "lib/constants";
import { validatePhoneNumber } from "lib/utils";
import UserSelect from "./userSelect";
import PubSub from "pubsub-js";

export default function Page() {
  const { user } = useContext(AuthContext);
  const phoneInputRef = useRef(null);
  const { selectedUser, setSelectedUser } = useContext(OrganizationContext);
  const [newUser, setNewUser] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    roleName: USER_ROLE.Technician,
    siteIds: [],
    customers: [],
  });
  const [originalUser, setOriginalUser] = useState(null);
  const [sites, setSites] = useState([]);

  useEffect(() => {
    const fetchUserData = async () => {
      if (selectedUser) {
        try {
          const response = await getUser(
            user.token,
            user.orgId,
            selectedUser.user.id,
          );
          const userData = response.data;
          const selectedUserData = {
            email: userData.user.email || "",
            firstName: userData.user.firstName || "",
            lastName: userData.user.lastName || "",
            phoneNumber: userData.user.phoneNumber || "",
            roleName: userData.user.role?.name || USER_ROLE.Technician,
            siteIds: userData.siteIds || [],
            customers: userData.customers || "",
          };
          console.log("selectedUserData:", selectedUserData);
          setNewUser(selectedUserData);
          setOriginalUser(selectedUserData);
        } catch (error) {
          console.error("Error fetching selected user data:", error);
        }
      } else {
        // Reset to default values if no user is selected
        setNewUser({
          email: "",
          firstName: "",
          lastName: "",
          phoneNumber: "",
          roleName: USER_ROLE.Technician,
          siteIds: [],
          customers: [],
        });
        setOriginalUser(null);
      }
    };

    fetchUserData();
  }, [selectedUser, user.token, user.orgId]);

  useEffect(() => {
    const fetchSiteData = async () => {
      try {
        const siteData = await fetchSites(user.token, user.orgId);
        setSites(siteData);
      } catch (error) {
        console.error("Error fetching sites:", error);
      }
    };

    fetchSiteData();
  }, [user.token, user.orgId]);

  const checkUserInfo = (info) => {
    if (
      info?.phoneNumber !== "" &&
      info?.phoneNumber !== "+" &&
      !validatePhoneNumber(info?.phoneNumber)
    ) {
      showToast(1, "Invalid phone number!");
      return false;
    }
    if (!info.email) {
      showToast(1, "Please enter the email!");
      return false;
    }
    if (!info.firstName || !info.lastName) {
      showToast(1, "Please enter the name!");
      return false;
    }
    return true;
  };

  const handleCreateUser = async () => {
    if (!checkUserInfo(newUser)) {
      return;
    }
    try {
      const userData = {
        email: newUser.email,
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        phoneNumber: newUser.phoneNumber,
        roleName: newUser.roleName,
        siteIds: newUser.siteIds,
        customers: newUser.customers,
      };
      await createUser(user.token, user.orgId, userData);
      setNewUser({
        email: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        roleName: USER_ROLE.Technician,
        siteIds: [],
        customers: [],
      });
      showToast(0, "User created successfully!");
      PubSub.publish("update-user-org");
    } catch (error) {
      console.error("Error creating user:", error);
      showToast(
        1,
        "There was an error creating the user. Please try again later.",
      );
    }
  };

  const handleUpdateUser = async () => {
    if (!checkUserInfo(newUser)) {
      return;
    }
    try {
      const userData = {
        email: newUser.email,
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        phoneNumber: newUser.phoneNumber,
        roleName: newUser.roleName,
        siteIds: newUser.siteIds,
        customers: newUser.customers,
      };
      await updateUser(user.token, user.orgId, selectedUser.user.id, userData);
      setSelectedUser(null);
      setNewUser({
        email: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        roleName: USER_ROLE.Technician,
        siteIds: [],
        customers: [],
      });
      showToast(0, "User updated successfully!");
      //mutate([`/org/users`, user.token, user.orgId]); // Revalidate organization users
      PubSub.publish("update-user-org");
    } catch (error) {
      console.error("Error updating user:", error);
      showToast(
        1,
        "There was an error updating the user. Please try again later.",
      );
    }
  };

  const handleDeleteUser = async () => {
    try {
      await deleteUser(user.token, user.orgId, selectedUser.user.id);
      showToast(0, "User deleted successfully!");
      setSelectedUser(null);
      setNewUser({
        email: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        roleName: USER_ROLE.Technician,
        siteIds: [],
        customers: [],
      });
      PubSub.publish("update-user-org");
    } catch (error) {
      console.error("Error deleting user:", error);
      showToast(
        1,
        "There was an error deleting the user. Please try again later.",
      );
    }
  };

  const hasChanges = () => {
    return JSON.stringify(newUser) !== JSON.stringify(originalUser);
  };

  const handleSiteChange = (siteId) => {
    const updatedSiteIds = newUser.siteIds.includes(siteId)
      ? newUser.siteIds.filter((id) => id !== siteId)
      : [...newUser.siteIds, siteId];
    setNewUser({ ...newUser, siteIds: updatedSiteIds });
  };

  return (
    <div className="flex flex-col items-center h-full p-6">
      <div className="w-full max-w-xl p-6 bg-white rounded">
        <h2 className="text-3xl font-semibold text-center mb-6">
          Organization Users
        </h2>
        <div className="grid grid-cols-1 gap-4 mb-6">
          <label className="font-medium">Email</label>
          <input
            type="email"
            value={newUser.email || ""}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            placeholder="Email"
            className="p-3 border rounded"
          />
          <label className="font-medium">First Name</label>
          <input
            type="text"
            value={newUser.firstName || ""}
            onChange={(e) =>
              setNewUser({ ...newUser, firstName: e.target.value })
            }
            placeholder="First Name"
            className="p-3 border rounded"
          />
          <label className="font-medium">Last Name</label>
          <input
            type="text"
            value={newUser.lastName || ""}
            onChange={(e) =>
              setNewUser({ ...newUser, lastName: e.target.value })
            }
            placeholder="Last Name"
            className="p-3 border rounded"
          />
          <label className="font-medium">Phone Number</label>
          <div className="h-[49px]">
            <PhoneInput
              value={newUser.phoneNumber || ""}
              onChange={(phone) => {
                setNewUser({ ...newUser, phoneNumber: phone });
              }}
            />
          </div>

          <label className="font-medium">Role</label>
          <select
            value={newUser.roleName || USER_ROLE.Technician}
            onChange={(e) =>
              setNewUser({ ...newUser, roleName: e.target.value as USER_ROLE })
            }
            className="p-3 border rounded"
          >
            {user?.role !== USER_ROLE.DealerAdmin && (
              <option value={USER_ROLE.DealerAdmin}>Dealer Admin</option>
            )}
            <option value={USER_ROLE.DealerRep}>Dealer Rep</option>
            <option value={USER_ROLE.Technician}>Technician</option>
            <option value={USER_ROLE.ProjectManager}>Project Manager</option>
            {user?.organization?.enableMonitoring && (
              <>
                <option value={USER_ROLE.Inspector}>Inspector</option>
                <option value={USER_ROLE.Dispatcher}>Dispatcher</option>
                <option value={USER_ROLE.Operator}>Operator</option>
                <option value={USER_ROLE.Scheduler}>Scheduler</option>
              </>
            )}
          </select>
          {newUser.roleName === USER_ROLE.ProjectManager && (
            <>
              <label className="font-medium">Customer</label>
              <UserSelect
                onChange={(value) => {
                  const _value = value.map((item) => ({
                    orgId: item.value,
                    accountName: item.label,
                  }));
                  setNewUser({ ...newUser, customers: _value });
                }}
                value={newUser.customers}
                formatter={(backendData) => {
                  if (!backendData) return [];
                  return backendData.map((item) => ({
                    value: item.orgId,
                    label: item.accountName,
                  }));
                }}
                maxSelectNumber={999}
              />
            </>
          )}
        </div>
        {newUser.roleName === USER_ROLE.SiteManager && (
          <div className="mb-6">
            <label className="font-medium">Assign Sites</label>
            <div className="grid grid-cols-2 gap-4">
              {Array.isArray(sites) &&
                sites.map((site) => (
                  <div key={site.id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newUser.siteIds.includes(site.id)}
                      onChange={() => handleSiteChange(site.id)}
                      className="mr-2"
                    />
                    <span>{site.name}</span>
                  </div>
                ))}
            </div>
          </div>
        )}
        <div className="flex justify-between">
          {selectedUser ? (
            <>
              <button
                onClick={handleDeleteUser}
                className="p-3 bg-red-500 text-white rounded hover:bg-red-400"
              >
                Deactivate
              </button>
              <button
                onClick={handleUpdateUser}
                className={`py-3 px-6 text-white rounded ${hasChanges() ? "bg-blue-500" : "bg-gray-300"}`}
                disabled={!hasChanges()}
              >
                Save
              </button>
            </>
          ) : (
            <button
              onClick={handleCreateUser}
              className="p-3 bg-blue-500 text-white rounded hover:bg-blue-400"
            >
              Create User
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
